'use client';
import { adyenPay, afterpayPayDetail, getGooglePaymentMethods } from '@/api/pay';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { accMul } from '@/utils/tools'; // 确保存在
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { OrderIdNo } from '../type';

interface GooglePayProps {
  from?: 'payment' | 'order' | 'vip';
  order_id?: string;
  createOrder?: (type: string) => Promise<{ order_id: string; order_no: string }>;
  grandTotal: number | string;
  shipping: { country_id: string };
  order?: any;
  googlePayDomId?: string;
  googlePayDom3ds?: string;
  isUseWallet?: number;
  payAmountWithWallet?: string;
  onPaySuccess?: (order: any) => void;
  onPayFail?: (order: OrderIdNo) => void;
  onValid?: (valid: boolean) => void;
  ref?: React.RefObject<any>;
}

const GooglePay: React.FC<GooglePayProps> = ({
  from = '',
  order_id,
  createOrder,
  grandTotal,
  shipping,
  order,
  googlePayDomId = '#google-pay-container',
  googlePayDom3ds = '#google-pay-3ds-container',
  isUseWallet = 2,
  payAmountWithWallet = '0.00',
  onPaySuccess,
  onPayFail,
  onValid,
  ref,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  const [orderId, setOrderId] = useState<string | number>(order_id || '');
  const checkoutInstanceRef = useRef<any>(null);
  const orderRef = useRef<any>(null); // 用于存储完整的订单数据
  const googlePayComponent = useRef<any>(null);
  const currency = useStore((state) => state.currency);

  useImperativeHandle(ref, () => ({
    payAction: (isTrigger: boolean, orderData?: any) => payAction(isTrigger, orderData),
  }));

  useEffect(() => {
    const initializeComponent = async () => {
      await initSdk();
      await payAction(false);
    };
    initializeComponent();
  }, []);

  const initSdk = (): Promise<void> => {
    return new Promise((resolve) => {
      if ((window as any)?.google?.payments) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.onload = () => resolve();
      script.onerror = () => resolve(); // 即使加载失败也继续执行
      document.head.appendChild(script);
    });
  };

  const getPaymentMethod = async () => {
    try {
      const { data } = await getGooglePaymentMethods({
        country_id: shipping.country_id || env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
        currency: from === 'order' ? order?.purchase_currency : currency.name,
        pay_type: 'googlepay',
      });
      setPaymentMethod(data);
      return data;
    } catch (err) {
      logger.error('getPaymentMethod error:', err);
      return null;
    }
  };

  const payAction = async (isTrigger: boolean, orderData?: any) => {
    const method = paymentMethod || (await getPaymentMethod());
    if (!method) return;

    let _orderData: any;

    if (isTrigger) {
      if (from === 'order') {
        setOrderId(order_id);
      } else if (orderData) {
        // 使用传入的订单数据
        _orderData = orderData;
        setOrderId(orderData.order_id);
      } else if (createOrder) {
        _orderData = await createOrder('googlepay');
        if (_orderData) {
          setOrderId(_orderData.order_id);
        } else {
          onPayFail?.(_orderData);
          return;
        }
      }
    }

    const configuration = {
      showPayButton: true,
      paymentMethodsResponse: method,
      clientKey: env.NEXT_PUBLIC_AYDEN_CLIENT_KEY,
      locale: env.NEXT_PUBLIC_FULL_LOCALE,
      environment: env.NEXT_PUBLIC_AYDEN_ENVIROMENT,
      onSubmit: async (state: any) => {
        try {
          // 保存订单数据到 ref，供 3DS 流程使用
          orderRef.current = _orderData;

          const res: any = await adyenPay({
            order_id: _orderData?.order_id || orderId,
            payment_method: JSON.stringify(state.data.paymentMethod),
            payment_type: 'googlepay',
            is_bind: 2,
          });
          if (res && res.code === 200) {
            if (res.data.action) {
              has3ds(res.data);
            } else {
              onPaySuccess?.(_orderData);
            }
          } else {
            logger.error('GooglePay payment failed:', res);
            onPayFail?.(_orderData);
          }
        } catch (error) {
          logger.error('GooglePay payment error:', error);
          onPayFail?.(_orderData);
        }
      },
      onAdditionalDetails,
      onChange: () => {},
      onError: (error: any) => {
        logger.error('GooglePay onError:', error);
        onPayFail?.(_orderData);
      },
    };

    const payAmount =
      isUseWallet === 1 && parseFloat(payAmountWithWallet) > 0 ? payAmountWithWallet : grandTotal;

    const googlepayConfig = {
      environment: process.env.NEXT_PUBLIC_ENV === 'production' ? 'PRODUCTION' : 'TEST',
      total: {
        label: 'NEXTMARVEL INC',
        type: 'final',
        amount: accMul(payAmount, 100),
      },
      amount: {
        value: accMul(payAmount, 100),
        currency: from === 'order' ? order?.purchase_currency : currency.name,
      },
      countryCode: shipping.country_id,
      currencyCode: from === 'order' ? order?.purchase_currency : currency.name,
      configuration: {
        merchantName: 'NEXTMARVEL INC',
        gatewayMerchantId: env.NEXT_PUBLIC_GOOGLE_GATEWAY_MERCHANT_ID,
        merchantId: env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_ID,
      },
      buttonType: '',
    };

    try {
      const checkout = new AdyenCheckout(configuration);

      // 等待 AdyenCheckout 初始化完成
      checkout
        .then((adyenInstance) => {
          checkoutInstanceRef.current = adyenInstance;
          googlePayComponent.current = adyenInstance.create('googlepay', googlepayConfig);

          googlePayComponent.current
            .isAvailable()
            .then((available: boolean) => {
              logger.log('GooglePay available:', available);
              if (available) {
                onValid?.(true);
                if (isTrigger) {
                  setTimeout(() => {
                    googlePayComponent.current.mount(googlePayDomId);
                  }, 0);
                }
              } else {
                onValid?.(false);
              }
            })
            .catch((error) => {
              logger.error('GooglePay isAvailable error:', error);
              onValid?.(false);
            });
        })
        .catch((error) => {
          logger.error('AdyenCheckout initialization error:', error);
          onValid?.(false);
        });
    } catch (error) {
      logger.error('GooglePay payAction error:', error);
      onValid?.(false);
    }
  };

  const has3ds = (data: any) => {
    if (data.action) {
      // 触发 3DS 验证
      const threeDSConfiguration = {
        challengeWindowSize: '05',
      };
      checkoutInstanceRef.current
        .createFromAction(data.action, threeDSConfiguration)
        .mount(googlePayDom3ds);
    }
  };

  const onAdditionalDetails = async (state: any) => {
    try {
      const currentOrderData = orderRef.current;
      const res = await afterpayPayDetail(state.data.details);

      if (res && res.code === 200) {
        onPaySuccess?.(currentOrderData);
      } else {
        logger.error('3DS verification failed:', res);
        onPayFail?.(currentOrderData);
      }
    } catch (error) {
      logger.error('3DS verification error:', error);
      onPayFail?.(orderRef.current);
    }
  };

  return <div className="xms-googlepay" />;
};

export default GooglePay;
